import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../../commons/api/api_service";
import BaseResponse from "../../../../commons/api/base_response";
import { Endpoints } from "../../../../commons/api/endpoints";
import { ProviderVM } from "../../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../../commons/scaffold/scaffold";
import StringUtils from "../../../../commons/utils/string_utils";
import {
    ItemOptions,
    ProductionAdjustmentModel,
    ProductionDetailResponse,
    ProductionResponse,
    ProductionSingleResponse,
    SubmitAdjustmentModel,
    SubmitAdjustmentResponse,
} from "../models/adjustment_forms_production_models";

export default class AdjustmentFormsProductionViewModel extends ProviderVM {
    // UI states
    isLoading: boolean = false;
    errorMessage: string = "";
    showSnackbar?: ShowSnackbarModel;
    showErrorView?: ShowErrorViewModel;
    isShowModal: boolean = false;

    // Data
    productionDataList?: ProductionSingleResponse[];
    productionDataMap: Map<string, ProductionAdjustmentModel> = new Map<
        string,
        ProductionAdjustmentModel
    >();
    editedProductionData?: ProductionSingleResponse;
    itemOptions?: ItemOptions[];
    addedItemIds: number[] = [];
    searchQuery: string = "";

    selectedItemId?: number;
    formId: number = 0;
    formLocationId: number = 0;

    constructor() {
        super();
        this.setup();
    }

    setup() {
        this.fetchProductionDataList();
        this.fetchItemOptions();
    }

    fetchProductionDataList() {
        this.setIsLoading(true);

        ApiService.fetch(Endpoints.adjustmentFormGetProductionList, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<ProductionResponse>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const list = response.data?.data as ProductionSingleResponse[];

                    for (let el of list) {
                        const date = new Date(el.receivedAt)
                        date.setTime(date.getTime() + 1000 * 60 * 60 * 7)
                        el.receivedAt = date.toISOString();
                    }

                    this.setProductionDataList(
                        list
                    );
                    this.setFormLocationId(response.data?.locationId ?? 0);

                    

                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    fetchProductionDetail(productionId: number) {
        this.setIsLoading(true);

        ApiService.fetch(
            `${Endpoints.adjustmentFormGetProductionDetail}/${productionId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<ProductionDetailResponse[]>
                ) => {
                    this.setIsLoading(false);
                    if (response.code === 200) {
                        const productionDetail =
                            response.data as ProductionDetailResponse[];
                        this.resetInitialProductionDataMap();
                        this.setInitialProductionDataMap(productionDetail);
                        this.setAddedItemIds(
                            productionDetail.map(
                                (product) => product.masterItemId
                            )
                        );
                    } else {
                        console.log(response);
                        this.setErrorMessage(response.message);
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                            },
                            message: this.errorMessage,
                        });
                    }
                },
                onFailure: (error) => {
                    console.log(error);
                    this.setIsLoading(false);
                    this.setErrorMessage(
                        "Failed fetching data, please refresh or try again"
                    );
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                            this.setup();
                        },
                        message: this.errorMessage,
                    });
                },
            }
        );
    }

    fetchItemOptions() {
        this.setIsLoading(true);
        ApiService.fetch(Endpoints.adjustmentFormGetItemOptions, {
            httpMethod: HttpMethod.get,
            onSuccess: (response: BaseResponse<ItemOptions[]>) => {
                this.setIsLoading(false);
                if (response.code === 200) {
                    const itemOptions = response.data as ItemOptions[];
                    this.setItemOptions(itemOptions);
                } else {
                    console.log(response);
                    this.setErrorMessage(response.message);
                    this.setShowErrorView({
                        action: () => {
                            this.reset();
                            this.setShowErrorView(undefined);
                            this.setErrorMessage("");
                        },
                        message: this.errorMessage,
                    });
                }
            },
            onFailure: (error) => {
                console.log(error);
                this.setIsLoading(false);
                this.setErrorMessage(
                    "Failed fetching data, please refresh or try again"
                );
                this.setShowErrorView({
                    action: () => {
                        this.reset();
                        this.setShowErrorView(undefined);
                        this.setErrorMessage("");
                        this.setup();
                    },
                    message: this.errorMessage,
                });
            },
        });
    }

    setCurrentEditedProductionData(form: ProductionSingleResponse) {
        this.emit(() => {
            this.editedProductionData = form;
        });
    }

    setNewReceivedAt(value: string) {
        this.emit(() => {
            this.editedProductionData!.receivedAt = StringUtils.UTCtoLocale(value);
        });
    }

    setItemOptions(value: ItemOptions[]) {
        this.emit(() => {
            this.itemOptions = value;
        });
    }

    setAddedItemIds(value: number[]) {
        this.emit(() => {
            this.addedItemIds = value;
        });
    }

    setFormId(value: number) {
        this.emit(() => {
            this.formId = value;
        });
    }

    setFormLocationId(value: number) {
        this.emit(() => {
            this.formLocationId = value;
        });
    }

    // Data
    setProductionDataList(data: ProductionSingleResponse[]) {
        this.emit(() => {
            this.productionDataList = data;
        });
    }

    setSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    // Modal data
    setInitialProductionDataMap(
        productionDataList: ProductionDetailResponse[]
    ) {
        this.emit(() => {
            productionDataList.forEach((data) => {
                const uniqueId = `${data.rowId}-${data.masterItemId}`;
                this.productionDataMap.set(uniqueId, {
                    rowId: data.rowId,
                    itemId: data.masterItemId,
                    uniqueId: uniqueId,
                    isNew: false,
                    smallQty: data.qtySmallUnit,
                    bigQty: data.qtyBigUnit,
                    woQty: data.qtyWo,
                    itemName: data.masterItemName,
                    bigUnit: data.bigUnit,
                    smallUnit: data.smallUnit,
                });
            });
        });
    }

    resetInitialProductionDataMap() {
        this.emit(() => {
            this.productionDataMap = new Map<
                string,
                ProductionAdjustmentModel
            >();
        });
    }

    setProductionDataMap(
        productionDataMap: Map<string, ProductionAdjustmentModel>
    ) {
        this.emit(() => {
            this.productionDataMap = productionDataMap;
        });
    }

    handleItemChange(
        uniqueId: string,
        field: keyof ProductionAdjustmentModel,
        value: number
    ) {
        const updatedMap = new Map(this.productionDataMap);
        const existingItem = updatedMap.get(uniqueId);
        if (existingItem) {
            updatedMap.set(uniqueId, { ...existingItem, [field]: value });
            this.setProductionDataMap(updatedMap);
        }
    }

    handleAddItem(itemId: number) {
        if (!this.addedItemIds.includes(itemId)) {
            const updatedMap = new Map(this.productionDataMap);
            const selectedOption = this.itemOptions?.find(
                (option) => option.id === itemId
            );

            const randomNumber = Math.random();
            const uniqueId = `${randomNumber}-${itemId}`;

            updatedMap.set(uniqueId, {
                rowId: randomNumber,
                itemId,
                uniqueId: uniqueId,
                bigQty: 0,
                smallQty: 0,
                woQty: 0,
                itemName: selectedOption?.item,
                bigUnit: selectedOption?.bigUnit,
                smallUnit: selectedOption?.smallUnit,
                isNew: true,
            });
            this.setProductionDataMap(updatedMap);
            this.setAddedItemIds([...this.addedItemIds, itemId]);
            this.setSearchQuery(selectedOption?.item.toLowerCase() ?? "");
        }
    }

    setSelectedItemId(value: number) {
        this.emit(() => {
            this.selectedItemId = value;
        });
    }

    // UI States
    setIsLoading(value: boolean) {
        this.emit(() => {
            this.isLoading = value;
        });
    }

    setErrorMessage(value: string) {
        this.emit(() => {
            this.errorMessage = value;
        });
    }

    setShowErrorView(showErrorView?: ShowErrorViewModel) {
        this.emit(() => {
            this.showErrorView = showErrorView;
        });
    }

    setIsShowModal(value: boolean) {
        this.emit(() => {
            this.isShowModal = value;
        });
    }

    resetModal() {
        this.emit(() => {
            this.isShowModal = false;
            this.productionDataMap = new Map<
                string,
                ProductionAdjustmentModel
            >();
            this.addedItemIds = [];
            this.formId = 0;
            this.searchQuery = "";
        });
    }

    reset() {
        this.emit(() => {
            this.isLoading = false;
            this.productionDataList = [];
            this.productionDataMap = new Map<
                string,
                ProductionAdjustmentModel
            >();
            this.itemOptions = [];
            this.addedItemIds = [];
            this.searchQuery = "";
            this.selectedItemId = undefined;
            this.formId = 0;
            this.formLocationId = 0;
            this.errorMessage = "";
        });
    }

    handleSubmit() {
        Swal.fire({
            title: "Konfirmasi",
            text: "Anda yakin ingin melakukan adjustment ini?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then((result) => {
            if (result.isConfirmed) {
                const items = [...this.productionDataMap.values()].map(
                    (value): ProductionAdjustmentModel => ({
                        rowId: value.rowId,
                        itemId: value.itemId,
                        uniqueId: value.uniqueId,
                        isNew: value.isNew,
                        bigQty: value.bigQty,
                        smallQty: value.smallQty,
                        woQty: value.woQty
                    })
                );

                const submitData: SubmitAdjustmentModel = {
                    formId: this.formId,
                    formLocation: this.formLocationId,
                    formType: "PRODUCTION",
                    receivedAt: StringUtils.LocaleToUTC(this.editedProductionData!.receivedAt),
                    items,
                };

                this.setIsLoading(true);
                ApiService.fetch(Endpoints.adjustmentFormSubmit, {
                    httpMethod: HttpMethod.post,
                    parameters: submitData,
                    onSuccess: (
                        response: BaseResponse<SubmitAdjustmentResponse>
                    ) => {
                        this.setIsLoading(false);
                        if (response.code === 200) {
                            const data =
                                response.data as SubmitAdjustmentResponse;
                            console.log(data);
                            this.showSnackbar = new ShowSnackbarModel({
                                message: "Success Adjust Production Form",
                                type: "normal",
                            });
                        } else {
                            console.log(response);
                            this.setErrorMessage(response.message);
                            this.setShowErrorView({
                                action: () => {
                                    this.reset();
                                    this.setShowErrorView(undefined);
                                    this.setErrorMessage("");
                                },
                                message: this.errorMessage,
                            });
                        }
                        this.resetModal();
                        this.reset();
                        this.setup();
                    },
                    onFailure: (error) => {
                        console.log(error);
                        this.setIsLoading(false);
                        this.setErrorMessage(
                            "Failed fetching data, please refresh or try again"
                        );
                        this.setShowErrorView({
                            action: () => {
                                this.reset();
                                this.setShowErrorView(undefined);
                                this.setErrorMessage("");
                                this.setup();
                            },
                            message: this.errorMessage,
                        });
                    },
                });
            }
        });
    }
}
